import { StackScreenProps } from "@react-navigation/stack";
import React, { useState } from "react";

import { useViewport } from "@/contexts/ViewportContext";
import { useDebounce } from "@/hooks/application/debounce";
import { useAppNavigation } from "@/hooks/application/navigation";
import { useInStoreSearch } from "@/hooks/domain/search";
import { Product } from "@/interfaces/domain/Product";
import { RootNavigation } from "@/navigation/Root";
import { HomeStackParamList } from "@/navigation/stacks/HomeStack";

import { SortTypesEnum } from "../types";
import InStoreSearchMobile from "./InStoreSearch.mobile";
import InStoreSearchWeb from "./InStoreSearch.web";

type ScreenProps = StackScreenProps<HomeStackParamList, "inStoreSearch">;

const InStoreSearch = ({ route }: ScreenProps) => {
  const routeQuery = route?.params?.query;
  const storeId = route?.params?.storeId;
  const { isWebMode } = useViewport();
  const { goBack } = useAppNavigation<RootNavigation>();

  const [query, setQuery] = useState<string | undefined>(routeQuery);
  const [sortedProducts, setSortedProducts] = useState<Product[]>(null);
  const debouncedQuery = useDebounce(query, 1000);

  const { data, isLoading, isFetching, hasNextPage, fetchNextPage } = useInStoreSearch({
    query: debouncedQuery,
    storeId,
    options: {
      enabled: debouncedQuery?.length >= 3 && !!storeId,
      onSuccess: (data) => {
        setSortedProducts(data?.pages.map((page) => page.produtos).flat());
      },
    },
  });

  const handleSearch = (text: string) => {
    setQuery(text);
  };

  const onSelectSortType = (type: SortTypesEnum) => {
    const sorted = [...data];
    switch (type) {
      case SortTypesEnum.NAME_DESC:
        sorted.sort((a, b) => (a.nome > b.nome ? 1 : -1));
        break;
      case SortTypesEnum.PRICE_LOWER:
        sorted.sort((a, b) => {
          const priceA = a.preco.preco_encarte ? a.preco.preco_encarte : a.preco.preco_original;
          const priceB = b.preco.preco_encarte ? b.preco.preco_encarte : b.preco.preco_original;
          const fullPriceA = a.tipo_embalagem === "kg" ? priceA / a.peso : priceA;
          const fullPriceB = b.tipo_embalagem === "kg" ? priceB / b.peso : priceB;
          return fullPriceA > fullPriceB ? 1 : -1;
        });
        break;
      default:
        break;
    }
    setSortedProducts([...sorted]);
  };

  const onLoadMore = () => {
    if (hasNextPage) {
      fetchNextPage();
    }
  };

  const Screen = isWebMode ? InStoreSearchWeb : InStoreSearchMobile;

  return (
    <Screen
      products={sortedProducts}
      storeId={storeId}
      isLoading={isLoading && isFetching}
      currentSearch={query}
      handleSearch={handleSearch}
      onSelectSortType={onSelectSortType}
      onBackPress={goBack}
      onLoadMore={onLoadMore}
    />
  );
};

export default InStoreSearch;
